import type { FC, SVGProps } from 'react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { useTranslation } from 'next-i18next';
import IcFilterBold from 'src/assets/images/icFilterBold.svg';
import IcPageSettingsBold from 'src/assets/images/icPageSettingsBold.svg';
import IcRefreshBold from 'src/assets/images/icRefreshBold.svg';
import IcVideoOffBold from 'src/assets/images/icVideoOffBold.svg';
import { hexToRgb } from 'src/utils/common';
import getDeviceInfo from 'src/utils/device/info';
const PermissionErrorGuideContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 40px;
  color: ${({
  theme
}) => theme.color.surface9};
`;
const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 50px;
  height: 50px;
`;
const growCircle1 = keyframes`
  0%, 100% {
    opacity: 0;
  }
  33.33%, 66.67% {
    opacity: 1;
  }
`;
const growCircle2 = keyframes`
  0%, 66.67%, 100% {
    opacity: 0;
  }
  33.33% {
    opacity: 1;
  }
`;
const CircleAnimation = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;

  &:before,
  &:after {
    content: '';
    display: block;
    width: 0;
    height: 0;
    background-color: rgba(${({
  theme
}) => hexToRgb(theme.color.primary3)}, 0.2);
    border-radius: 50%;
    position: absolute;
    opacity: 0;
    animation-timing-function: ease-in-out;
    animation-iteration-count: infinite;
    animation-fill-mode: forwards;
  }

  &:before {
    width: 34px;
    height: 34px;
    animation-delay: 600ms;
    animation-duration: 1800ms;
    animation-name: ${growCircle1};
  }

  &:after {
    width: 50px;
    height: 50px;
    animation-delay: 1200ms;
    animation-duration: 1800ms;
    animation-name: ${growCircle2};
  }
`;
const Icon = styled.svg`
  width: 18px;
  height: 18px;
  color: ${({
  theme
}) => theme.color.content6};
`;
const Text = styled.div`
  font-size: 24px;
  text-align: center;
  ${({
  theme
}) => theme.screenSize.tablet} {
    font-size: 12px;
  }
`;
type PermissionGuide = {
  icon: FC<SVGProps<SVGSVGElement>>;
  i18nKey: string;
};
const PERMISSION_GUIDE_MAP: Record<string, PermissionGuide> = {
  desktop_chrome: {
    icon: IcFilterBold,
    i18nKey: 'CAMERA_ALLOW_GUIDE'
  },
  desktop_safari: {
    icon: IcPageSettingsBold,
    i18nKey: 'CAMERA_ALLOW_GUIDE_SAFARI'
  },
  desktop_firefox: {
    icon: IcFilterBold,
    i18nKey: 'CAMERA_ALLOW_GUIDE'
  },
  desktop_default: {
    icon: IcVideoOffBold,
    i18nKey: 'CAMERA_ALLOW_GUIDE'
  },
  mobile_safari: {
    icon: IcPageSettingsBold,
    i18nKey: 'CAMERA_ALLOW_GUIDE_SAFARI'
  },
  mobile_default: {
    icon: IcRefreshBold,
    i18nKey: 'CAMERA_ALLOW_GUIDE_MOBILE'
  }
};
const getPermissionGuide = (browserName: string, isMobile: boolean): PermissionGuide => {
  const keyPrefix = isMobile ? 'mobile' : 'desktop';
  return PERMISSION_GUIDE_MAP[`${keyPrefix}_${browserName}`] ?? PERMISSION_GUIDE_MAP[`${keyPrefix}_default`];
};
const PermissionErrorGuide = () => {
  const {
    t
  } = useTranslation();
  const {
    browserName = '',
    isMobile = false
  } = getDeviceInfo();
  const guide = getPermissionGuide(browserName, isMobile);
  return <PermissionErrorGuideContainer data-sentry-element="PermissionErrorGuideContainer" data-sentry-component="PermissionErrorGuide" data-sentry-source-file="Guide.tsx">
      <IconContainer data-sentry-element="IconContainer" data-sentry-source-file="Guide.tsx">
        <CircleAnimation data-sentry-element="CircleAnimation" data-sentry-source-file="Guide.tsx" />
        <Icon as={guide.icon} data-sentry-element="Icon" data-sentry-source-file="Guide.tsx" />
      </IconContainer>
      <Text data-sentry-element="Text" data-sentry-source-file="Guide.tsx">{t(guide.i18nKey)}</Text>
    </PermissionErrorGuideContainer>;
};
export default PermissionErrorGuide;